import type { FC } from 'react';

import type { SettingsFormValues } from '../hooks/useSettingsPage.hook';

interface FieldProps {
  label: string;
  value: string;
  error?: string;
  suffix?: string;
  placeholder?: string;
  hint?: string;
  onChange: (value: string) => void;
}

const Field: FC<FieldProps> = ({ label, value, error, suffix, placeholder, hint, onChange }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
      {label}
    </span>
    <div className="flex items-center gap-2">
      <input
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-lg border bg-white px-3 py-2 text-[13px] text-[var(--color-ink)] outline-none focus:border-brand-300 ${
          error ? 'border-red-400' : 'border-[var(--color-divider)]'
        }`}
      />
      {suffix ? <span className="text-[13px] text-[var(--color-ink-muted)]">{suffix}</span> : null}
    </div>
    {hint ? <span className="text-[11px] text-[var(--color-ink-muted)]">{hint}</span> : null}
    {error ? <span className="text-[11px] text-red-600">{error}</span> : null}
  </label>
);

interface SettingsFormProps {
  form: SettingsFormValues;
  errors: Partial<Record<keyof SettingsFormValues, string>>;
  isUploadingQr: boolean;
  uploadError: string | null;
  onChange: (field: keyof SettingsFormValues, value: string) => void;
  onUploadQrImage: (file: File) => void;
  onClearQrImage: () => void;
}

const SettingsForm: FC<SettingsFormProps> = ({
  form,
  errors,
  isUploadingQr,
  uploadError,
  onChange,
  onUploadQrImage,
  onClearQrImage,
}) => (
  <div className="flex flex-col gap-6">
    <section className="rounded-2xl border border-[var(--color-divider)] bg-white p-5">
      <h2 className="text-[15px] font-bold text-[var(--color-ink)]">Fees</h2>
      <p className="mb-4 text-[12px] text-[var(--color-ink-muted)]">
        Applied to new bookings only; existing bookings keep the rate snapshotted at creation.
      </p>
      <div className="grid gap-4 md:grid-cols-3">
        <Field
          label="Commission (borrower)"
          value={form.commissionPct}
          error={errors.commissionPct}
          suffix="%"
          hint="Added on top of the rental"
          onChange={(v) => onChange('commissionPct', v)}
        />
        <Field
          label="GST (on fees)"
          value={form.gstPct}
          error={errors.gstPct}
          suffix="%"
          hint="On commission + settlement fee"
          onChange={(v) => onChange('gstPct', v)}
        />
        <Field
          label="Settlement fee (owner)"
          value={form.settlementFeePct}
          error={errors.settlementFeePct}
          suffix="%"
          hint="Deducted from owner payout"
          onChange={(v) => onChange('settlementFeePct', v)}
        />
      </div>
    </section>

    <section className="rounded-2xl border border-[var(--color-divider)] bg-white p-5">
      <h2 className="text-[15px] font-bold text-[var(--color-ink)]">UPI collection</h2>
      <p className="mb-4 text-[12px] text-[var(--color-ink-muted)]">
        Shown to borrowers on the payment screen (deep-link + QR).
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <Field
          label="UPI ID (VPA)"
          value={form.upiId}
          error={errors.upiId}
          placeholder="shoutt@icici"
          onChange={(v) => onChange('upiId', v)}
        />
        <Field
          label="Payee name"
          value={form.payeeName}
          error={errors.payeeName}
          placeholder="Shoutt"
          onChange={(v) => onChange('payeeName', v)}
        />
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
            Static QR image
          </span>
          <div className="flex items-center gap-3">
            {form.qrImageUrl ? (
              <img
                src={form.qrImageUrl}
                alt="QR"
                className="h-16 w-16 rounded-lg border border-[var(--color-divider)] object-contain"
              />
            ) : null}
            <div className="flex flex-col gap-1">
              <label className="inline-flex cursor-pointer items-center rounded-lg border border-[var(--color-divider)] bg-white px-3 py-2 text-[12px] font-semibold text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]">
                {isUploadingQr ? 'Uploading…' : form.qrImageUrl ? 'Replace image' : 'Upload from device'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={isUploadingQr}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) onUploadQrImage(file);
                    e.target.value = '';
                  }}
                />
              </label>
              {form.qrImageUrl ? (
                <button
                  type="button"
                  onClick={onClearQrImage}
                  className="text-left text-[11px] text-red-600 hover:underline"
                >
                  Remove
                </button>
              ) : null}
            </div>
          </div>
          <span className="text-[11px] text-[var(--color-ink-muted)]">
            Optional. A backup QR borrowers can scan from another device — their main option is the
            &ldquo;Pay via UPI&rdquo; button.
          </span>
          {uploadError ? <span className="text-[11px] text-red-600">{uploadError}</span> : null}
          {errors.qrImageUrl ? (
            <span className="text-[11px] text-red-600">{errors.qrImageUrl}</span>
          ) : null}
        </div>
        <Field
          label="Internal note (which bank account receives the money)"
          value={form.bankNote}
          error={errors.bankNote}
          placeholder="e.g. ICICI current a/c ••1234"
          hint="Only your team sees this — helps match payments to the bank statement."
          onChange={(v) => onChange('bankNote', v)}
        />
      </div>
    </section>
  </div>
);

export default SettingsForm;
